import { Suspense, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
    isRouteErrorResponse,
    Navigate,
    Outlet,
    ScrollRestoration,
    useLocation,
    useRouteError,
} from 'react-router';
import type { RouteObject } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import type { TFunction } from 'i18next';
import { Box, CssBaseline } from '@mui/material';
import config from '../../config/config.json';
import captureReactRouteError from './Common/Routing/captureReactRouteError';
import Account from './Account/Account';
import BrandingOptionList from './Account/BrandingOptions/BrandingOptionList';
import ImageList from './Account/Images/ImageList';
import OrderList from './Account/Orders/OrderList';
import OrderPage from './Account/Orders/OrderPage';
import OrderPageOverview from './Account/Orders/OrderPageOverview';
import OrderPagePayment from './Account/Orders/OrderPagePayment';
import OrderPageShipping from './Account/Orders/OrderPageShipping';
import PhotoLabCreationList from './Account/PhotoLab/PhotoLabCreationList';
import ProductDesignList from './Account/Products/ProductDesignList';
import AddressList from './Account/Settings/Addresses/AddressList';
import CreditCardList from './Account/Settings/CreditCards/CreditCardList';
import Info from './Account/Settings/Info';
import Notifications from './Account/Settings/Notifications';
import Security from './Account/Settings/Security';
import TaxNumberList from './Account/Settings/TaxNumbers/TaxNumberList';
import StoreConnect from './Account/Stores/Connect/StoreConnect';
import BigCartelConnect from './Account/Stores/Connect/BigCartelConnect';
import EtsyConnect from './Account/Stores/Connect/EtsyConnect';
import ShopifyConnect from './Account/Stores/Connect/ShopifyConnect';
import SquarespaceConnect from './Account/Stores/Connect/SquarespaceConnect';
import StoreProductList from './Account/Stores/Products/StoreProductList';
import StoreList from './Account/Stores/StoreList';
import StoreOverview from './Account/Stores/StoreOverview';
import StorePresets from './Account/Stores/StorePresets';
import StorePurchaseReport from './Account/Stores/StorePurchaseReport';
import StoreSettings from './Account/Stores/StoreSettings';
import StoreOrderList from './Account/Stores/Orders/StoreOrderList';
import StoreOrderPage from './Account/Stores/Orders/StoreOrderPage';
import StoreOrderPageOverview from './Account/Stores/Orders/StoreOrderPageOverview';
import StoreOrderPagePayment from './Account/Stores/Orders/StoreOrderPagePayment';
import StoreOrderPageShipping from './Account/Stores/Orders/StoreOrderPageShipping';
import ApplicationDataLayer from './AppData/_components/ApplicationDataLayer';
import AccountLink from './Auth/AccountLink';
import AccountNotValidated from './Auth/AccountNotValidated';
import AccountValidated from './Auth/AccountValidated';
import ForgotPasswordPage from './Auth/ForgotPasswordPage';
import LoginPage from './Auth/LoginPage';
import ResetPasswordPage from './Auth/ResetPasswordPage';
import SessionMismatchDialog from './Auth/SessionMismatchDialog';
import SignedUpPage from './Auth/SignedUpPage';
import SignupPage from './Auth/SignupPage';
import Cart from './Cart/Cart';
import CartConfirmation from './Cart/Confirmation/CartConfirmation';
import CartOverview from './Cart/Overview/CartOverview';
import CartPayment from './Cart/Payment/CartPayment';
import CartShipping from './Cart/Shipping/CartShipping';
import PageLoader from './Common/Loader/PageLoader';
import { NavigationProgressLine, usePageNavigationFeedback } from './Common/NavigationFeedback';
import NotFoundPage from './Common/NotFoundPage';
import { isRouteNotFoundError } from './Common/Routing/RouteNotFoundError';
import DesignLab from './DesignLab/_components/DesignLab';
import Footer from './Footer/Footer';
import Header from './Header/Header';
import SamplePacks from './Info/SamplePacks';
import InvoicePage from './Invoice/InvoicePage';
import InvoicePageOverview from './Invoice/InvoicePageOverview';
import InvoicePagePayment from './Invoice/InvoicePagePayment';
import SnapbackWithPrintedPatchPage from './Product/Accessories/Headwear/SnapbackWithPrintedPatchPage';
import DadCapWithPrintedPatchPage from './Product/Accessories/Headwear/DadCapWithPrintedPatchPage';
import PrintedPatchPage from './Product/Accessories/Patches/PrintedPatchPage';
import MatteCrepeRuffleScrunchiePage from './Product/Accessories/Headwear/MatteCrepeRuffleScrunchiePage';
import SilkCharmeuseRuffleScrunchiePage from './Product/Accessories/Headwear/SilkCharmeuseRuffleScrunchiePage';
import TwillyScarfPage from './Product/Accessories/Scarves/TwillyScarfPage';
import CottonSilkBandanasPage from './Product/Accessories/Scarves/CottonSilkBandanasPage';
import BabyLeggingsPage from './Product/BabyAndYouth/BabyLeggingsPage';
import YouthLeggingsPage from './Product/BabyAndYouth/YouthLeggingsPage';
import DrawstringBagPage from './Product/BagsAndTotes/DrawstringBagPage';
import PremiumDrawstringBagPage from './Product/BagsAndTotes/PremiumDrawstringBagPage';
import VeganLeatherBackpackPage from './Product/BagsAndTotes/VeganLeatherBackpackPage';
import CaprisPage from './Product/Bottoms/LeggingsAndPants/CaprisPage';
import LeggingsPage from './Product/Bottoms/LeggingsAndPants/LeggingsPage';
import UnisexJoggersPage from './Product/Bottoms/LeggingsAndPants/UnisexJoggersPage';
import YogaCaprisPage from './Product/Bottoms/LeggingsAndPants/YogaCaprisPage';
import YogaLeggingsPage from './Product/Bottoms/LeggingsAndPants/YogaLeggingsPage';
import GlitterYogaShortsPage from './Product/Bottoms/Shorts/GlitterYogaShortsPage';
import RunnerShortsPage from './Product/Bottoms/Shorts/RunnerShortsPage';
import ShortsPage from './Product/Bottoms/Shorts/ShortsPage';
import YogaShortsPage from './Product/Bottoms/Shorts/YogaShortsPage';
import FittedSkirtPage from './Product/Bottoms/Skirts/FittedSkirtPage';
import FlareSkirtPage from './Product/Bottoms/Skirts/FlareSkirtPage';
import GlitterFittedSkirtPage from './Product/Bottoms/Skirts/GlitterFittedSkirtPage';
import GlitterFlareSkirtPage from './Product/Bottoms/Skirts/GlitterFlareSkirtPage';
import BodyconDressPage from './Product/DressesAndRobes/Dresses/BodyconDressPage';
import FlareDressPage from './Product/DressesAndRobes/Dresses/FlareDressPage';
import StainlessSteelTumblerPage from './Product/HomeDecor/BottlesAndTumblers/StainlessSteelTumblerPage';
import StainlessSteelWaterBottlePage from './Product/HomeDecor/BottlesAndTumblers/StainlessSteelWaterBottlePage';
import CottonSateenTableclothPage from './Product/HomeDecor/Kitchen/CottonSateenTableclothPage';
import LinenLookTableclothPage from './Product/HomeDecor/Kitchen/LinenLookTableclothPage';
import PetBowlPage from './Product/HomeDecor/Kitchen/PetBowlPage';
import PlacematPage from './Product/HomeDecor/Kitchen/PlacematPage';
import VeganLeatherPlacematPage from './Product/HomeDecor/Kitchen/VeganLeatherPlacematPage';
import CoasterPage from './Product/HomeDecor/LivingRoom/CoasterPage';
import PolyCanvasPillowCasePage from './Product/HomeDecor/LivingRoom/PolyCanvasPillowCasePage';
import VelveteenPillowCasePage from './Product/HomeDecor/LivingRoom/VelveteenPillowCasePage';
import DeskMatPage from './Product/HomeDecor/Office/DeskMatPage';
import AluminumPrintPage from './Product/HomeDecor/WallArt/AluminumPrintPage';
import PermanentStickerPage from './Product/Stationery/PermanentStickerPage';
import AthleticCropTopPage from './Product/Tops/TankTops/AthleticCropTopPage';
import GlitterAthleticCropTopPage from './Product/Tops/TankTops/GlitterAthleticCropTopPage';
import FittedPoloShirtPage from './Product/Tops/TShirts/FittedPoloShirtPage';
import UnisexPoloShirtPage from './Product/Tops/TShirts/UnisexPoloShirtPage';
import BoxerBriefsPage from './Product/Underwear/BoxerBriefsPage';
import BoyshortsPage from './Product/Underwear/BoyshortsPage';
import CheekyBriefsPage from './Product/Underwear/CheekyBriefsPage';
import ScoopBralettePage from './Product/Underwear/ScoopBralettePage';
import ModelViewerEditor from './ModelViewer/Standalone/Editor/App';
import ProductCatalog from './ProductCatalog/ProductCatalog';

interface RouteTranslationsHandle {
    translations?: string[]
}

type WebsiteRouteObject = RouteObject & {
    handle?: RouteTranslationsHandle
    children?: WebsiteRouteObject[]
}

type RouteComponent = NonNullable<RouteObject['Component']>

const uniqueTranslations = (translations: string[]) => Array.from(new Set(translations))

const translatedRoute = (path: string, Component: RouteComponent, translations: string[]): WebsiteRouteObject => ({
    path,
    Component,
    handle: {
        translations: uniqueTranslations(translations)
    }
})

const productPageRoute = (path: string, Component: RouteComponent, translation: string): WebsiteRouteObject => (
    translatedRoute(path, Component, [
        'page/products/_sections',
        'page/products/' + translation
    ])
)

export const WebsiteRoutes = (tR:TFunction): WebsiteRouteObject[] => [
    { 
        path: '/',
        Component: Website,
        errorElement: <WebsiteRouteErrorBoundary />,
        children: [
            productPageRoute(tR('/products/bottoms/shorts/yoga-shorts'), YogaShortsPage, 'yoga-shorts'),
            productPageRoute(tR('/products/bottoms/shorts/glitter-yoga-shorts'), GlitterYogaShortsPage, 'glitter-yoga-shorts'),
            productPageRoute(tR('/products/tops/tank-tops/glitter-athletic-crop-tops'), GlitterAthleticCropTopPage, 'glitter-athletic-crop-top'),
            productPageRoute(tR('/products/tops/tshirts/unisex-polo-shirts'), UnisexPoloShirtPage, 'unisex-polo-shirts'),
            productPageRoute(tR('/products/tops/tshirts/fitted-polo-shirts'), FittedPoloShirtPage, 'fitted-polo-shirts'),
            productPageRoute(tR('/products/bottoms/skirts/glitter-fitted-skirts'), GlitterFittedSkirtPage, 'glitter-fitted-skirt'),
            productPageRoute(tR('/products/bottoms/skirts/glitter-flare-skirts'), GlitterFlareSkirtPage, 'glitter-flare-skirt'),
            productPageRoute(tR('/products/stationery/permanent-stickers'), PermanentStickerPage, 'permanent-stickers'),
            productPageRoute(tR('/products/accessories/patches/printed-patches'), PrintedPatchPage, 'printed-patches'),
            productPageRoute(tR('/products/accessories/headwear/snapback-with-printed-patch'), SnapbackWithPrintedPatchPage, 'snapback-with-printed-patch'),
            productPageRoute(tR('/products/accessories/headwear/matte-crepe-ruffle-scrunchies'), MatteCrepeRuffleScrunchiePage, 'matte-crepe-ruffle-scrunchies'),
            productPageRoute(tR('/products/accessories/headwear/silk-charmeuse-ruffle-scrunchies'), SilkCharmeuseRuffleScrunchiePage, 'silk-charmeuse-ruffle-scrunchies'),
            productPageRoute(tR('/products/accessories/headwear/dad-cap-with-printed-patch'), DadCapWithPrintedPatchPage, 'dad-cap-with-printed-patch'),
            productPageRoute(tR('/products/bags-and-totes/backpacks/vegan-leather-backpacks'), VeganLeatherBackpackPage, 'vegan-leather-backpack'),
            productPageRoute(tR('/products/underwear/cheeky-briefs'), CheekyBriefsPage, 'cheeky-briefs'),
            productPageRoute(tR('/products/underwear/boyshorts'), BoyshortsPage, 'boyshorts'),
            productPageRoute(tR('/products/underwear/boxer-briefs'), BoxerBriefsPage, 'boxer-briefs'),
            productPageRoute(tR('/products/underwear/scoop-bralettes'), ScoopBralettePage, 'scoop-bralette'),
            productPageRoute(tR('/products/bags-and-totes/backpacks/premium-drawstring-bags'), PremiumDrawstringBagPage, 'premium-drawstring-bags'),
            productPageRoute(tR('/products/bags-and-totes/backpacks/drawstring-bags'), DrawstringBagPage, 'drawstring-bags'),
            productPageRoute(tR('/products/home-and-living/bottles-and-tumblers/stainless-steel-tumblers'), StainlessSteelTumblerPage, 'stainless-steel-tumblers'),
            productPageRoute(tR('/products/home-and-living/bottles-and-tumblers/stainless-steel-water-bottles'), StainlessSteelWaterBottlePage, 'stainless-steel-water-bottles'),
            //{ path: tR('/products/home-and-living/wall-art/canvases'), Component: CanvasPrintPage},
            productPageRoute(tR('/products/home-and-living/office/desk-mats'), DeskMatPage, 'desk-mats'),
            productPageRoute(tR('/products/home-and-living/wall-art/aluminum-prints'), AluminumPrintPage, 'aluminum-prints'),
            productPageRoute(tR('/products/bottoms/leggings-and-pants/unisex-joggers'), UnisexJoggersPage, 'unisex-joggers'),
            productPageRoute(tR('/products/home-and-living/living-room/coasters'), CoasterPage, 'coasters'),
            productPageRoute(tR('/products/home-and-living/living-room/poly-canvas-pillow-cases'), PolyCanvasPillowCasePage, 'poly-canvas-pillow-cases'),
            productPageRoute(tR('/products/home-and-living/living-room/velveteen-pillow-cases'), VelveteenPillowCasePage, 'velveteen-pillow-cases'),
            productPageRoute(tR('/products/home-and-living/kitchen-and-dining/placemats'), PlacematPage, 'placemats'),
            productPageRoute(tR('/products/home-and-living/kitchen-and-dining/vegan-leather-placemats'), VeganLeatherPlacematPage, 'vegan-leather-placemats'),
            productPageRoute(tR('/products/home-and-living/kitchen-and-dining/pet-bowls'), PetBowlPage, 'pet-bowls'),
            productPageRoute(tR('/products/home-and-living/kitchen-and-dining/linen-look-tablecloths'), LinenLookTableclothPage, 'linen-look-tablecloths'),
            productPageRoute(tR('/products/home-and-living/kitchen-and-dining/cotton-sateen-tablecloths'), CottonSateenTableclothPage, 'cotton-sateen-tablecloths'),
            productPageRoute(tR('/products/tops/tank-tops/athletic-crop-tops'), AthleticCropTopPage, 'athletic-crop-top'),
            productPageRoute(tR('/products/baby-and-youth/baby-leggings'), BabyLeggingsPage, 'baby-leggings'),
            productPageRoute(tR('/products/dresses-and-robes/dresses/bodycon-dresses'), BodyconDressPage, 'bodycon-dress'),
            productPageRoute(tR('/products/bottoms/leggings-and-pants/capris'), CaprisPage, 'capris'),
            productPageRoute(tR('/products/bottoms/leggings-and-pants/leggings'), LeggingsPage, 'leggings'),
            productPageRoute(tR('/products/bottoms/skirts/fitted-skirts'), FittedSkirtPage, 'fitted-skirt'),
            productPageRoute(tR('/products/bottoms/skirts/flare-skirts'), FlareSkirtPage, 'flare-skirt'),
            productPageRoute(tR('/products/dresses-and-robes/dresses/flare-dresses'), FlareDressPage, 'flare-dress'),
            productPageRoute(tR('/products/bottoms/shorts/shorts'), ShortsPage, 'shorts'),
            productPageRoute(tR('/products/bottoms/shorts/runner-shorts'), RunnerShortsPage, 'runner-shorts'),
            productPageRoute(tR('/products/bottoms/leggings-and-pants/yoga-capris'), YogaCaprisPage, 'yoga-capris'),
            productPageRoute(tR('/products/bottoms/leggings-and-pants/yoga-leggings'), YogaLeggingsPage, 'yoga-leggings'),
            productPageRoute(tR('/products/baby-and-youth/youth-leggings'), YouthLeggingsPage, 'youth-leggings'),
            productPageRoute(tR('/products/accessories/scarves/twilly-scarves'), TwillyScarfPage, 'twilly-scarves'),
            productPageRoute(tR('/products/accessories/scarves/cotton-silk-bandanas'), CottonSilkBandanasPage, 'cotton-silk-bandanas'),

            translatedRoute(tR('/products/:category1?/:category2?'), ProductCatalog, ['products']),
            translatedRoute(tR('/create/:product?'), DesignLab, ['design-lab', 'products']),
            {
                path: tR('/account'),
                Component: Account,
                handle: {
                    translations: ['account-v2']
                },
                children: [
                    { path: tR('/account'), element: <Navigate to={tR('/account/products')} /> },
                    { path: tR('/account/products'), Component: ProductDesignList },
                    { path: tR('/account/images'), Component: ImageList },
                    { path: tR('/account/orders'), Component: OrderList },
                    {
                        path: tR('/account/orders/:refId'),
                        Component: OrderPage,
                        handle: {
                            translations: ['cart']
                        },
                        children: [
                            { path: tR('/account/orders/:refId/'), Component: OrderPageOverview },
                            { path: tR('/account/orders/:refId/shipping'), Component: OrderPageShipping },
                            { path: tR('/account/orders/:refId/payment'), Component: OrderPagePayment }
                        ]
                    },
                    { path: tR('/account/branding-options'), Component: BrandingOptionList },
                    { path: tR('/account/photo-lab'), Component: PhotoLabCreationList },
                    { path: tR('/account/settings/addresses'), Component: AddressList },
                    { path: tR('/account/settings/credit-cards'), Component: CreditCardList },
                    { path: tR('/account/settings/tax-numbers'), Component: TaxNumberList },
                    { path: tR('/account/settings/security'), Component: Security },
                    { path: tR('/account/settings/info'), Component: Info },
                    { path: tR('/account/settings/notifications'), Component: Notifications },
                    { path: tR('/account/stores'), Component: StoreList },
                    { 
                        path: tR('/account/stores/connect'), 
                        Component: StoreConnect,
                        children: [
                            { path: tR('/account/stores/connect/shopify'), Component: ShopifyConnect },
                            { path: tR('/account/stores/connect/etsy'), Component: EtsyConnect },
                            { path: tR('/account/stores/connect/bigcartel'), Component: BigCartelConnect },
                            { path: tR('/account/stores/connect/squarespace'), Component: SquarespaceConnect },
                        ]
                    },
                    { path: tR('/account/stores/:storeSlug/overview'), Component: StoreOverview },
                    { path: tR('/account/stores/:storeSlug/settings'), Component: StoreSettings },
                    { path: tR('/account/stores/:storeSlug/presets'), Component: StorePresets },
                    { path: tR('/account/stores/:storeSlug/reports'), Component: StorePurchaseReport },
                    { path: tR('/account/stores/:storeSlug/products'), Component: StoreProductList },
                    { path: tR('/account/stores/:storeSlug/orders'), Component: StoreOrderList },
                    {
                        path: tR('/account/stores/:storeSlug/orders/:refId'),
                        Component: StoreOrderPage,
                        handle: {
                            translations: ['cart']
                        },
                        children: [
                            { path: tR('/account/stores/:storeSlug/orders/:refId/'), Component: StoreOrderPageOverview },
                            { path: tR('/account/stores/:storeSlug/orders/:refId/shipping'), Component: StoreOrderPageShipping },
                            { path: tR('/account/stores/:storeSlug/orders/:refId/payment'), Component: StoreOrderPagePayment }
                        ]
                    },
                ]
            },
            {
                path: tR('/cart'),
                Component: Cart,
                handle: {
                    translations: ['cart']
                },
                children: [
                    { path: tR('/cart'), Component: CartOverview },
                    { path: tR('/cart/shipping'), Component: CartShipping },
                    { path: tR('/cart/payment'), Component: CartPayment },
                ]
            },
            translatedRoute(tR('/cart/confirmation'), CartConfirmation, ['cart']),
            { path: tR('/info/sample-packs'), Component: SamplePacks },
            {
                path: tR('/invoice/:invoiceId'),
                Component: InvoicePage,
                handle: {
                    translations: ['account-v2', 'cart']
                },
                children: [
                    { path: tR('/invoice/:invoiceId/'), Component: InvoicePageOverview },
                    { path: tR('/invoice/:invoiceId/payment'), Component: InvoicePagePayment }
                ]
            },
            translatedRoute(tR('/auth/login'), LoginPage, ['auth']),
            translatedRoute(tR('/auth/signup'), SignupPage, ['auth']),
            translatedRoute(tR('/auth/signed-up'), SignedUpPage, ['auth']),
            translatedRoute(tR('/auth/forgot-password'), ForgotPasswordPage, ['auth']),
            translatedRoute(tR('/auth/reset-password'), ResetPasswordPage, ['auth']),
            translatedRoute(tR('/auth/validate-account'), AccountValidated, ['auth']),
            translatedRoute(tR('/auth/account-not-validated'), AccountNotValidated, ['auth']),
            translatedRoute(tR('/auth/signup-confirm-account-link'), AccountLink, ['auth']),
            ...(config.ENVIRONMENT === 'dev'
                ? [translatedRoute(tR('/e/3d-viewer'), ModelViewerEditor, ['common'])]
                : []),
            translatedRoute('*', NotFoundPage, ['common']),
            //{ path: tR('/dev-playground'), Component: DevPlayground },
        ]
    },
]

interface WebsiteLayoutProps {
    children: ReactNode
}

function WebsiteLayout({ children }: WebsiteLayoutProps) {
    const [tR] = useTranslation('routes');
    const location = useLocation();
    //const showHeader = !location.pathname.startsWith(tR('/create'));
    const showHeader = !location.pathname.startsWith(tR('/create'))
        && !(
            config.ENVIRONMENT === 'dev'
            && location.pathname.startsWith(tR('/e/'))
        );
    const showPageNavigationFeedback = usePageNavigationFeedback(showHeader)

    return <ApplicationDataLayer>
        <Helmet
            titleTemplate="%s | Art of Where"
            defaultTitle="Art of Where | Drop ship custom leggings, scarves and more"
        >

        </Helmet>
        <CssBaseline>
            {showHeader ? <Header clientRouterLinks /> : null}
            {showHeader ? <NavigationProgressLine className={showPageNavigationFeedback ? 'active' : undefined} /> : null}
            <Box
                component="div"
                aria-busy={showPageNavigationFeedback}
                sx={{
                    display: 'flex',
                    flex: '1 0 auto',
                    flexDirection: 'column',
                }}
            >
                {children}
                {showHeader ? <Footer clientRouterLinks /> : null}
            </Box>
            <SessionMismatchDialog />
            <ScrollRestoration />
        </CssBaseline>
    </ApplicationDataLayer>
}

function WebsiteRouteErrorBoundary() {
    const error = useRouteError();
    const location = useLocation();
    const notFound = isRouteNotFoundError(error) || (
        isRouteErrorResponse(error) && error.status === 404
    );
    const path = location.pathname + location.search + location.hash;

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production' || notFound) {
            return;
        }

        captureReactRouteError(error, {
            boundary: 'website-route',
            path,
        });
    }, [error, notFound, path]);

    if (notFound) {
        return <WebsiteLayout>
            <NotFoundPage />
        </WebsiteLayout>
    }

    if (process.env.NODE_ENV !== 'production') {
        if (error instanceof Error) {
            throw error;
        }

        throw new Error('Unexpected route error');
    }

    return <></>
}

export default function Website() {
    return <WebsiteLayout>
        <Suspense fallback={<PageLoader />}>
            <Outlet />
        </Suspense>
    </WebsiteLayout>
}
